import React from "react";
import ReactDOM from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import * as ProcessActions from "../../redux/actions/process.actions";

const BOOL_OPTS = ["NO", "YES"];

function IconVars() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h16M4 12h10M4 18h14" />
    </svg>
  );
}
function IconClose() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}
function IconPlus() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function IconUp() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );
}
function IconDown() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function Field({ label, value, onChange, disabled }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </label>
      <input
        type="text"
        value={value || ""}
        onChange={onChange}
        disabled={disabled}
        style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", border: "1.5px solid #e5e7eb", borderRadius: 7, fontSize: 13, background: disabled ? "#f9fafb" : "#fff", color: disabled ? "#9ca3af" : "#111827", outline: "none" }}
        onFocus={e => { if (!disabled) e.target.style.borderColor = "#6366f1"; }}
        onBlur={e => { e.target.style.borderColor = "#e5e7eb"; }}
      />
    </div>
  );
}

function SelectField({ label, value, opts, onChange }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 10.5, fontWeight: 700, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </label>
      <select
        value={value || "NO"}
        onChange={onChange}
        style={{ width: "100%", padding: "7px 8px", border: "1.5px solid #e5e7eb", borderRadius: 7, fontSize: 12.5, background: "#fff", color: "#374151", outline: "none", cursor: "pointer" }}
      >
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function VarsPanel({ onClose, id, roles, header, selectedRecord, rows, requesting, dispatch }) {
  const roleOpts = React.useMemo(() => {
    const opts = ["NO", "ALL"];
    (roles || []).forEach(r => { if (r.USERGROUPNAME && !opts.includes(r.USERGROUPNAME)) opts.push(r.USERGROUPNAME); });
    return opts;
  }, [roles]);

  const [vars, setVars] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [expandedIdx, setExpandedIdx] = React.useState(null);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);
  React.useEffect(() => {
    if (!loaded && rows && rows.length > 0) {
      setVars(rows.map(r => ({ ...r })));
      setLoaded(true);
    }
  }, [rows, loaded]);

  const entityLabel = React.useMemo(() => {
    if (!selectedRecord) return String(id);
    const vals = Object.values(selectedRecord);
    return vals.length > 1 ? String(vals[1] || id) : String(id);
  }, [selectedRecord, id]);

  const addVar = () => {
    const newVar = { VARNAME: "", VARLABEL: "", EDITABLE: "NO", REMARKABLE: "NO", HIDDEN: "NO", DEFVALUE: "", _isNew: true };
    setVars(prev => [...prev, newVar]);
    setExpandedIdx(vars.length);
  };

  const removeVar = (idx) => {
    setVars(prev => prev.filter((_, i) => i !== idx));
    setExpandedIdx(null);
  };

  const moveVar = (idx, dir) => {
    const target = idx + dir;
    setVars(prev => {
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setExpandedIdx(prev => prev === idx ? target : prev === target ? idx : prev);
  };

  const update = (idx, key, val) => {
    setVars(prev => prev.map((v, i) => i === idx ? { ...v, [key]: val } : v));
  };

  const handleSave = () => {
    const flowvars = vars.map((v, i) => ({ ...v, SORTORDER: i + 1 }));
    dispatch(ProcessActions.processData("flows", "set-flow-vars", { id, flowvars }, header));
    handleClose();
  };

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 220);
  };

  return ReactDOM.createPortal(
    <>
      <div onClick={handleClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9998, opacity: visible ? 1 : 0, transition: "opacity 0.22s ease" }} />

      <div style={{ position: "fixed", top: 0, right: 0, width: 480, height: "100vh", background: "#fff", zIndex: 9999, display: "flex", flexDirection: "column", boxShadow: "-6px 0 40px rgba(0,0,0,0.18)", fontFamily: '"72","72full",Arial,Helvetica,sans-serif', transform: visible ? "translateX(0)" : "translateX(100%)", transition: "transform 0.24s cubic-bezier(0.4,0,0.2,1)" }}>

        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #0d1117 0%, #1e1b4b 100%)", color: "#f1f5f9", padding: "20px 20px 16px", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <IconVars />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>Variables del Flujo</div>
                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2, letterSpacing: 0.3 }}>Editar variables y propiedades</div>
              </div>
            </div>
            <button onClick={handleClose} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", padding: "5px 6px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <IconClose />
            </button>
          </div>

          <div style={{ marginTop: 14, background: "rgba(255,255,255,0.1)", borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, background: "rgba(255,255,255,0.22)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
              {String(entityLabel).charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 10, opacity: 0.65, letterSpacing: 0.5, textTransform: "uppercase" }}>Flujo</div>
              <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}>{entityLabel}</div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #e5e7eb", background: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            {vars.length} variable{vars.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={addVar}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 14px", background: "#0f172a", border: "none", borderRadius: 7, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
          >
            <IconPlus /> Agregar
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {requesting ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 160, gap: 12, color: "#9ca3af" }}>
              <div style={{ width: 22, height: 22, border: "3px solid #e5e7eb", borderTopColor: "#6366f1", borderRadius: "50%", animation: "dlg-spin 0.7s linear infinite" }} />
              <span style={{ fontSize: 13 }}>Cargando...</span>
            </div>
          ) : vars.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 160, color: "#9ca3af", fontSize: 13 }}>
              No hay variables definidas — usa "Agregar" para crear una
            </div>
          ) : vars.map((v, idx) => {
            const isOpen = expandedIdx === idx;
            return (
              <div key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                {/* Row header */}
                <div
                  onClick={() => setExpandedIdx(isOpen ? null : idx)}
                  style={{ display: "flex", alignItems: "center", padding: "11px 16px", cursor: "pointer", background: isOpen ? "#eef2ff" : "transparent", userSelect: "none", transition: "background 0.12s" }}
                  onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = "#f9fafb"; }}
                  onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: isOpen ? "#4338ca" : "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.VARNAME || "(nueva variable)"}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.VARLABEL || "sin etiqueta"}{v.DEFVALUE ? ` · default: ${v.DEFVALUE}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 4, marginRight: 10, alignItems: "center" }}>
                    {v.REMARKABLE === "YES" && (
                      <span style={{ fontSize: 10, background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "1px 6px", fontWeight: 700 }}>★</span>
                    )}
                    {v.HIDDEN === "YES" && (
                      <span style={{ fontSize: 10, background: "#f3f4f6", color: "#6b7280", borderRadius: 4, padding: "1px 6px" }}>oculto</span>
                    )}
                    {v.EDITABLE && v.EDITABLE !== "NO" && (
                      <span style={{ fontSize: 10, background: "#dbeafe", color: "#1e40af", borderRadius: 4, padding: "1px 6px" }}>{v.EDITABLE}</span>
                    )}
                    <button
                      type="button"
                      disabled={idx === 0}
                      onClick={e => { e.stopPropagation(); moveVar(idx, -1); }}
                      style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 4, padding: "2px 4px", cursor: idx === 0 ? "default" : "pointer", color: idx === 0 ? "#d1d5db" : "#6b7280", display: "flex", alignItems: "center", lineHeight: 1 }}
                      title="Subir"
                    ><IconUp /></button>
                    <button
                      type="button"
                      disabled={idx === vars.length - 1}
                      onClick={e => { e.stopPropagation(); moveVar(idx, 1); }}
                      style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 4, padding: "2px 4px", cursor: idx === vars.length - 1 ? "default" : "pointer", color: idx === vars.length - 1 ? "#d1d5db" : "#6b7280", display: "flex", alignItems: "center", lineHeight: 1 }}
                      title="Bajar"
                    ><IconDown /></button>
                  </div>
                  <span style={{ color: "#9ca3af", fontSize: 10, flexShrink: 0 }}>{isOpen ? "▲" : "▼"}</span>
                </div>

                {/* Expanded form */}
                {isOpen && (
                  <div style={{ padding: "14px 16px 16px", background: "#f8fafc", borderTop: "1px solid #e5e7eb" }}>
                    <Field label="Nombre (VARNAME)" value={v.VARNAME} disabled={!v._isNew}
                      onChange={e => update(idx, "VARNAME", e.target.value)} />
                    <Field label="Etiqueta (VARLABEL)" value={v.VARLABEL}
                      onChange={e => update(idx, "VARLABEL", e.target.value)} />
                    <Field label="Valor por defecto (DEFVALUE)" value={v.DEFVALUE}
                      onChange={e => update(idx, "DEFVALUE", e.target.value)} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 6 }}>
                      <SelectField label="Editable" value={v.EDITABLE} opts={roleOpts}
                        onChange={e => update(idx, "EDITABLE", e.target.value)} />
                      <SelectField label="Highlight" value={v.REMARKABLE} opts={BOOL_OPTS}
                        onChange={e => update(idx, "REMARKABLE", e.target.value)} />
                      <SelectField label="Oculto" value={v.HIDDEN} opts={BOOL_OPTS}
                        onChange={e => update(idx, "HIDDEN", e.target.value)} />
                    </div>
                    <button
                      onClick={() => removeVar(idx)}
                      style={{ marginTop: 12, padding: "5px 12px", background: "transparent", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                    >
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 20px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 10, justifyContent: "flex-end", background: "#fff", flexShrink: 0 }}>
          <button
            onClick={handleClose}
            style={{ padding: "8px 20px", border: "1.5px solid #d1d5db", borderRadius: 7, background: "#fff", color: "#374151", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#9ca3af"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#d1d5db"; }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            style={{ padding: "8px 22px", border: "none", borderRadius: 7, background: "#4338ca", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}
          >
            Guardar variables
            <span style={{ background: "rgba(255,255,255,0.25)", borderRadius: 10, padding: "1px 7px", fontSize: 12 }}>{vars.length}</span>
          </button>
        </div>
      </div>

      <style>{`@keyframes dlg-spin { to { transform: rotate(360deg); } }`}</style>
    </>,
    document.body
  );
}

const DialogFlowVarsTemplate = (props) => {
  const { rows, requesting } = useSelector(state => state.Process);
  const dispatch = useDispatch();

  if (!props.status || props.id < 0) return null;

  return (
    <VarsPanel
      onClose={props.close}
      id={props.id}
      roles={props.roles || []}
      header={props.header}
      selectedRecord={props.selectedRecord}
      rows={rows}
      requesting={requesting}
      dispatch={dispatch}
    />
  );
};

DialogFlowVarsTemplate.defaultProps = { roles: [] };

export default DialogFlowVarsTemplate;
